import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Link } from 'react-router-dom';
import { Building2, FileText, Receipt, Wrench, TrendingUp, ArrowRight } from 'lucide-react';

export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [cantieri, setCantieri] = useState([]);
    const [preventivi, setPreventivi] = useState([]);
    const [kpi, setKpi] = useState({ fatturato_anno: 0, da_incassare: 0, scadute: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                // Seed demo data first (idempotent), then fetch
                await api.post('/demo/seed', {}).catch(() => {});
                const [c, p, k] = await Promise.all([
                    api.get('/cantieri/list'),
                    api.get('/preventivi/list'),
                    api.get('/fatture/kpi'),
                ]);
                setCantieri(c.data);
                setPreventivi(p.data);
                setKpi(k.data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const cantieriAttivi = cantieri.filter(c => c.stato === 'attivo');
    const importoCantieri = cantieri.reduce((s, c) => s + (c.importo || 0), 0);
    const avanzamentoMedio = cantieri.length > 0 ? cantieri.reduce((s, c) => s + (c.avanzamento || 0), 0) / cantieri.length : 0;

    return (
        <div data-testid="dashboard-page">
            <div className="mb-6">
                <div className="text-xs tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{t('dashboard.hello', { name: (user?.full_name || '').toUpperCase() })}</div>
                <h1 className="font-display text-4xl md:text-5xl">{t('dashboard.title')}</h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('dashboard.subtitle')}</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2"><div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{t('dashboard.kpi_cantieri')}</div><Building2 className="w-5 h-5 text-[hsl(var(--accent))]" /></div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-cantieri">{cantieriAttivi.length}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('dashboard.kpi_cantieri_sub', { total: cantieri.length })}</div>
                </Card>
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2"><div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{t('dashboard.kpi_avanz')}</div><TrendingUp className="w-5 h-5 text-[hsl(var(--accent))]" /></div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-avanzamento">{avanzamentoMedio.toFixed(0)}%</div>
                    <Progress value={avanzamentoMedio} className="mt-2 h-2" />
                </Card>
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2"><div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{t('dashboard.kpi_importo')}</div><Receipt className="w-5 h-5 text-[hsl(var(--accent))]" /></div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-importo">€{(importoCantieri/1000).toFixed(0)}k</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('dashboard.kpi_importo_sub')}</div>
                </Card>
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2"><div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{t('dashboard.kpi_incasso')}</div><Wrench className="w-5 h-5 text-[hsl(var(--accent))]" /></div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-incasso">€{(kpi.da_incassare/1000).toFixed(1)}k</div>
                    {kpi.scadute > 0 && <Badge className="bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)] hover:bg-[hsl(0_80%_95%)] mt-1">{t('dashboard.kpi_scadute', { val: kpi.scadute.toFixed(0) })}</Badge>}
                </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-2xl">{t('dashboard.sec_cantieri')}</h2>
                        <Link to="/app/sal"><Button variant="ghost" size="sm" className="gap-1" data-testid="dashboard-sal-link">{t('dashboard.all')} <ArrowRight className="w-4 h-4" /></Button></Link>
                    </div>
                    <div className="space-y-3">
                        {loading ? <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('dashboard.loading')}</div> : cantieriAttivi.length === 0 ? (
                            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                                <Building2 className="w-10 h-10 mx-auto mb-2" />
                                <p>{t('dashboard.no_cantieri')}</p>
                                <Link to="/app/sal"><Button size="sm" className="mt-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">{t('dashboard.create_first')}</Button></Link>
                            </div>
                        ) : cantieriAttivi.slice(0, 4).map((c) => (
                            <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))]">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold truncate">{c.nome}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.cliente}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-lg tabular-nums">{c.avanzamento || 0}%</div>
                                    <Progress value={c.avanzamento || 0} className="w-20 h-1.5" />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card className="p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-2xl">{t('dashboard.sec_preventivi')}</h2>
                        <Link to="/app/preventivi"><Button variant="ghost" size="sm" className="gap-1" data-testid="dashboard-prev-link">{t('dashboard.all')} <ArrowRight className="w-4 h-4" /></Button></Link>
                    </div>
                    <div className="space-y-3">
                        {preventivi.slice(0, 4).map((p) => (
                            <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))]">
                                <div className="min-w-0">
                                    <div className="font-semibold truncate">{p.numero} · {p.cliente}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{p.categoria}</div>
                                </div>
                                <div className="font-display text-lg tabular-nums">€{p.contenuto?.totale_finale?.toLocaleString('it-IT') || '0'}</div>
                            </div>
                        ))}
                        {preventivi.length === 0 && (
                            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                                <FileText className="w-10 h-10 mx-auto mb-2" />
                                <p>{t('dashboard.no_preventivi')}</p>
                                <Link to="/app/preventivi"><Button size="sm" className="mt-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]">{t('dashboard.create_ai')}</Button></Link>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}
